"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.WebSocketTransport = void 0;
const logger_1 = require("../interfaces/logger");
const utils_1 = require("../utils");
const constants_1 = require("./constants");
const baseWsTransport_1 = require("./baseWsTransport");
const DEFAULT_URL = 'wss://appbk.4pda.to/ws/';
const BROWSER_UPLOAD_ERROR = 'WebSocketTransport.upload is not supported in the browser. Use fourpda-proxy with uploadFileStandalone from fourpda-api/internal.';
class WebSocketTransport extends baseWsTransport_1.BaseWsTransport {
    constructor(autoReconnect = true) {
        super();
        this.ws = null;
        this.isReconnecting = false;
        this.reconnectAttempt = 0;
        this.connectPromise = null;
        this.shouldReconnect = autoReconnect;
        this.logger = new logger_1.ConsoleLogger();
    }
    setLogger(logger) {
        this.logger = logger;
    }
    isConnected() {
        return this.ws !== null && this.ws.readyState === WebSocket.OPEN;
    }
    async connect(url = DEFAULT_URL) {
        if (this.isConnected())
            return;
        if (this.connectPromise)
            return this.connectPromise;
        if (this.ws) {
            try {
                this.ws.onopen = null;
                this.ws.onmessage = null;
                this.ws.onerror = null;
                this.ws.onclose = null;
                this.ws.close();
            }
            catch (e) { }
            this.ws = null;
        }
        this.connectPromise = new Promise((resolve, reject) => {
            this.ws = new WebSocket(url);
            this.ws.binaryType = 'arraybuffer';
            this.ws.onopen = () => {
                this.logger.debug('[BrowserTransport] Connected');
                this.startKeepAlive(() => {
                    this.ws.send(this.buildKeepAlivePayload());
                });
                this.isReconnecting = false;
                this.reconnectAttempt = 0;
                this.emit('connected');
                resolve();
            };
            this.ws.onmessage = (event) => {
                this.logger.debug('[BrowserTransport] Received:', event.data);
                this.dispatchWireMessage(event.data, 'BrowserTransport');
            };
            this.ws.onerror = (err) => {
                const msg = '[BrowserTransport] Socket error occurred (Network change or timeout)';
                if (this.isReconnecting) {
                    this.logger.debug(msg);
                }
                else {
                    this.logger.warn(msg);
                }
                this.emit('error', err);
                if (this.ws?.readyState !== WebSocket.OPEN) {
                    reject(err);
                }
                else {
                    try {
                        this.ws.close();
                    }
                    catch (closeError) {
                        this.logger.debug('[BrowserTransport] Failed to close socket after error', closeError);
                    }
                }
            };
            this.ws.onclose = () => {
                this.logger.debug('[BrowserTransport] Disconnected');
                this.stopKeepAlive();
                this.ws = null;
                if (this.pendingRequests.size > 0) {
                    this.logger.warn(`[BrowserTransport] Rejecting ${this.pendingRequests.size} pending requests due to disconnect`);
                    this.rejectAllPending(new Error('Connection closed'));
                }
                this.emit('disconnected');
                if (this.shouldReconnect && !this.isReconnecting) {
                    this.handleReconnect(url);
                }
            };
        }).finally(() => {
            this.connectPromise = null;
        });
        return this.connectPromise;
    }
    disconnect() {
        this.shouldReconnect = false;
        this.stopKeepAlive();
        this.ws?.close();
    }
    /** Close socket without disabling auto-reconnect (e.g. browser went offline). */
    resetConnection() {
        this.stopKeepAlive();
        if (this.ws) {
            try {
                this.ws.close();
            }
            catch (e) {
                this.logger.debug('[BrowserTransport] resetConnection close failed', e);
            }
        }
    }
    async send(cmd, params = []) {
        if (!this.isConnected()) {
            if (this.shouldReconnect) {
                this.logger.debug(`[BrowserTransport] Waiting for connection to send: ${cmd}`);
                await this.ensureConnected();
            }
            else {
                throw new Error('Transport not connected');
            }
        }
        const id = this.allocateRequestId();
        const payload = [id, cmd, ...params];
        return new Promise((resolve, reject) => {
            this.trackPending(id, resolve, reject, constants_1.REQUEST_TIMEOUT_MS, `Request ${id} timed out`);
            try {
                this.ws.send((0, utils_1.encodePdaJson)(payload));
            }
            catch (e) {
                const err = e instanceof Error ? e : new Error(String(e));
                this.failPending(id, err);
                try {
                    this.ws?.close();
                }
                catch (closeError) {
                    this.logger.debug('[BrowserTransport] Failed to close socket after send error', closeError);
                }
            }
        });
    }
    /**
     * Chunked `su` upload is not available over browser WebSocket.
     * @see fourpda-api/PACKAGING.md — web upload via fourpda-proxy.
     */
    async upload(_cmd, _params, _fileData, _onProgress) {
        throw new Error(BROWSER_UPLOAD_ERROR);
    }
    async handleReconnect(url) {
        this.isReconnecting = true;
        this.reconnectAttempt++;
        const delay = Math.min(Math.pow(2, this.reconnectAttempt) * 1000, 30000);
        this.logger.info(`[BrowserTransport] Reconnecting in ${delay / 1000}s (attempt ${this.reconnectAttempt})...`);
        setTimeout(async () => {
            try {
                if (!this.shouldReconnect) {
                    this.isReconnecting = false;
                    return;
                }
                await this.connect(url);
            }
            catch {
                if (this.shouldReconnect) {
                    this.handleReconnect(url);
                }
                else {
                    this.isReconnecting = false;
                }
            }
        }, delay);
    }
    async ensureConnected() {
        if (this.isConnected())
            return;
        return new Promise((resolve, reject) => {
            let timeoutId = null;
            const onConnected = () => {
                this.off('connected', onConnected);
                this.off('error', onError);
                if (timeoutId)
                    clearTimeout(timeoutId);
                resolve();
            };
            const onError = (err) => {
                this.off('connected', onConnected);
                this.off('error', onError);
                if (timeoutId)
                    clearTimeout(timeoutId);
                reject(err instanceof Error ? err : new Error(String(err)));
            };
            this.on('connected', onConnected);
            this.on('error', onError);
            timeoutId = setTimeout(() => {
                this.off('connected', onConnected);
                this.off('error', onError);
                if (!this.isConnected()) {
                    reject(new Error('Нет подключения к серверу 4PDA. Проверьте сеть.'));
                    return;
                }
                try {
                    this.ws?.close();
                }
                catch (closeError) {
                    this.logger.debug('[BrowserTransport] Failed to close socket after connect timeout', closeError);
                }
                reject(new Error('Нет подключения к серверу 4PDA. Проверьте сеть.'));
            }, 15000);
        });
    }
}
exports.WebSocketTransport = WebSocketTransport;
